import { memo, useContext, useCallback, useMemo, useRef } from 'react';
import classnames from 'classnames';
import { Avatar, Text, Icon, IconMultiple, useTheme, Popover, Spacer } from '@Sage/hoi-poi-ui';
import { getLiteral } from 'utils/getLiteral';
import { findNodeByType } from '../../../../utils';
import { isEmptyObject } from 'utils/objects';

import DeleteModal from 'components/DeleteModal';
import { AutomationBuilderCanvasContext } from '../../AutomationBuilderCanvasContext';
import AutomationBuilderAddNodesButton from '../AutomationBuilderAddNodesButton';

import './styles.scss';

const AutomationBuilderBox = memo(
    ({
        id,
        className,
        title,
        icon,
        secondIcon,
        emptyPlaceholder,
        textValue,
        children,
        isEnd,
        allowDelete,
        isEveryTime,
        isIncomplete,
    }) => {
        const theme = useTheme();
        const { state, dispatch, data, onDeleteNode } = useContext(AutomationBuilderCanvasContext);
        const deleteModalRef = useRef();
        const onSelect = useCallback(
            (e) => {
                e.stopPropagation();
                dispatch({
                    type: state.selected ? 'nextSelect' : 'select',
                    node: findNodeByType(data?.scene, id),
                });
            },
            [data?.scene, dispatch, id, state.selected],
        );

        const isDisabled = useMemo(
            () => isEmptyObject(data.scene?.parameters || {}) && id !== data.scene?.id,
            [data?.scene, id],
        );

        const showPlaceholder = !textValue && emptyPlaceholder && !children;
        const avatarIcon = useMemo(() => {
            if (!secondIcon) return <Avatar icon={icon} size="big" />;
            return <IconMultiple firstIcon={icon} secondIcon={secondIcon} size="large" />;
        }, [icon, secondIcon]);

        const onDeleteRef = useCallback((ref) => {
            deleteModalRef.current = ref;
        }, []);

        const openDeleteModal = useCallback((e) => {
            e.stopPropagation();
            deleteModalRef.current.open();
        }, []);

        const onDeleteConfirm = useCallback(() => {
            onDeleteNode(id);
            return Promise.resolve();
        }, [id, onDeleteNode]);

        const popoverEveryTimeProps = useMemo(() => {
            return {
                trigger: ['hover'],
                content: (
                    <div>
                        <Text type="subtitle" medium>
                            {getLiteral('label_automations_execution_mode')}
                        </Text>
                        <Spacer y={2} />
                        <div className="fm-automation-builder__box__every-time__title">
                            <Icon name={isEveryTime ? 'automationsRepeat' : 'automationsOnce'} />
                            <Text>
                                {isEveryTime
                                    ? getLiteral('label_automations_execution_mode_run_every_time')
                                    : getLiteral('label_automations_execution_mode_run_once')}
                            </Text>
                        </div>

                        <Spacer y={1} />
                        <Text color={theme.colors.textLight.secondary}>
                            {isEveryTime
                                ? getLiteral('label_automations_execution_mode_run_every_time_desc')
                                : getLiteral('label_automations_execution_mode_run_once_desc')}
                        </Text>
                    </div>
                ),
            };
        }, [isEveryTime, theme.colors.textLight.secondary]);

        return (
            <div className="fm-automation-builder__box-container">
                <div
                    className={classnames({
                        'fm-automation-builder__box--disabled': isDisabled,
                        'fm-automation-builder__box--incomplete': isIncomplete,
                    })}
                >
                    <div
                        className={classnames('fm-automation-builder__box', className, {
                            'fm-automation-builder__box--selected': state.selected?.id === id,
                        })}
                        onClick={isDisabled || state.selected?.name === id ? undefined : onSelect}
                    >
                        <div className="fm-automation-builder__box__header">
                            <div className="fm-automation-builder__box__header-title">
                                {avatarIcon}
                                <Text medium>{title}</Text>
                            </div>
                            <div className="fm-automation-builder__box__header-icons">
                                {allowDelete && (
                                    <Icon
                                        className="fm-automation-builder__box__delete"
                                        tooltip={getLiteral('action_delete')}
                                        onClick={openDeleteModal}
                                        name="delete"
                                    />
                                )}
                                {isEveryTime !== undefined && (
                                    <Popover {...popoverEveryTimeProps}>
                                        <span>
                                            <Icon
                                                name={
                                                    isEveryTime
                                                        ? 'automationsRepeat'
                                                        : 'automationsOnce'
                                                }
                                            />
                                        </span>
                                    </Popover>
                                )}
                            </div>
                        </div>
                        <div className="fm-automation-builder__box__content">
                            {textValue && (
                                <Text type="subtitle" medium>
                                    {textValue}
                                </Text>
                            )}
                            {showPlaceholder && (
                                <Text type="subtitle" medium>
                                    {emptyPlaceholder}
                                </Text>
                            )}
                            {children}
                        </div>
                    </div>
                </div>
                {isEnd && (
                    <div className="fm-automation-builder__box__add-nodes">
                        <div className="fm-automation-builder__box__add-nodes-line" />
                        <AutomationBuilderAddNodesButton parentId={id} />
                    </div>
                )}
                <DeleteModal
                    overlayClassName="fm-automation-builder__modal"
                    title={getLiteral('title_delete')}
                    body={getLiteral('label_title_delete_registry_modal')}
                    confirmText={getLiteral('action_delete')}
                    onRef={onDeleteRef}
                    onDelete={onDeleteConfirm}
                />
            </div>
        );
    },
);

export default AutomationBuilderBox;
